/**
 * API client for backend
 * Includes automatic token refresh via HttpOnly cookie.
 * FIXED: Better error handling to prevent silent logout during checkout
 */
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Send cookies with every request
});

// Track whether a refresh is already in flight to avoid duplicate calls
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });
  failedQueue = [];
};

// Request interceptor - add auth token with debug logging
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    } else {
      console.warn('[API] No token found in localStorage for request:', config.url);
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - handle 401 with automatic refresh
// FIXED: Don't auto-logout on checkout failures - let the user see the error
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const requestUrl = originalRequest?.url || '';

    // Don't retry auth endpoints or already-retried requests
    const isAuthEndpoint = requestUrl.includes('/auth/login')
      || requestUrl.includes('/auth/register')
      || requestUrl.includes('/auth/refresh');

    // CRITICAL FIX: Don't auto-logout for checkout endpoints
    // Let the user see the actual error and retry
    const isCheckoutEndpoint = requestUrl.includes('/secure-checkout') 
      || requestUrl.includes('/packages/checkout');

    if (error.response?.status === 401 && !isAuthEndpoint && !originalRequest._retry) {
      // For checkout endpoints, don't trigger logout - just reject with error
      // This prevents the "silent logout" bug
      if (isCheckoutEndpoint) {
        console.error('[API] 401 on checkout - NOT auto-logging out. Error:', error.response?.data);
        // Attempt one refresh, but don't redirect on failure
        try {
          const { data } = await axios.post(
            `${API_BASE}/auth/refresh`,
            {},
            { withCredentials: true }
          );
          const newToken = data.access_token;
          localStorage.setItem('token', newToken);
          localStorage.setItem('user', JSON.stringify(data.user));
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return api(originalRequest);
        } catch (refreshError) {
          console.error('[API] Refresh failed for checkout, returning original error');
          // Return the original error - don't logout
          return Promise.reject(error);
        }
      }

      if (isRefreshing) {
        // Another request is already refreshing - queue this one
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const { data } = await axios.post(
          `${API_BASE}/auth/refresh`,
          {},
          { withCredentials: true }
        );

        const newToken = data.access_token;
        localStorage.setItem('token', newToken);
        localStorage.setItem('user', JSON.stringify(data.user));

        api.defaults.headers.common.Authorization = `Bearer ${newToken}`;
        originalRequest.headers.Authorization = `Bearer ${newToken}`;

        processQueue(null, newToken);
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        // Refresh failed - session is truly expired.
        // P0 UX fix: preserve current location so Login can redirect back
        // after re-auth (Login.jsx already consumes ?redirect=<path>).
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        try {
          const current = window.location.pathname + window.location.search + window.location.hash;
          // Hotfix: PIN-based pages (Karishma/Rohan/Mark) redirect to /pin (light theme),
          // NOT /login (dark theme = "black screen" complaint). Same instruction prefills label.
          const pinMap = [
            { prefix: '/portal/karishma', label: 'karishma' },
            { prefix: '/driver/vehicle-log', label: 'rohan' },
            { prefix: '/admin/vehicle-ops', label: 'mark' },
          ];
          const pinHit = pinMap.find((m) => current.startsWith(m.prefix));
          // Don't create redirect loops when we're already on /login or /select-role
          const skip = current.startsWith('/login') || current.startsWith('/select-role') || current.startsWith('/pin');
          if (pinHit) {
            window.location.href = '/pin?label=' + pinHit.label;
          } else if (!skip && current && current !== '/') {
            sessionStorage.setItem('post_login_redirect', current);
            window.location.href = '/login?redirect=' + encodeURIComponent(current);
          } else {
            window.location.href = '/login';
          }
        } catch (_e) {
          window.location.href = '/login';
        }
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  register: async (data) => {
    const response = await api.post('/auth/register', data);
    return response.data;
  },
  login: async (identifier, password) => {
    const response = await api.post('/auth/login', { identifier, password });
    return response.data;
  },
  getMe: async () => {
    const response = await api.get('/auth/me');
    return response.data;
  },
  logout: async () => {
    try {
      await api.post('/auth/logout');
    } catch (e) {
      // Best-effort server logout
    }
  },
};

// Users API
export const usersAPI = {
  list: async (skip = 0, limit = 50) => {
    const response = await api.get(`/users/?skip=${skip}&limit=${limit}`);
    return response.data;
  },
  get: async (id) => {
    const response = await api.get(`/users/${id}`);
    return response.data;
  },
  updateRoles: async (id, roles) => {
    const response = await api.patch(`/users/${id}/roles`, roles);
    return response.data;
  },
  deactivate: async (id) => {
    const response = await api.post(`/users/${id}/deactivate`);
    return response.data;
  },
  activate: async (id) => {
    const response = await api.post(`/users/${id}/activate`);
    return response.data;
  },
};

// Admin API
export const adminAPI = {
  test: async () => {
    const response = await api.get('/admin/test');
    return response.data;
  },
  bootstrap: async (secretKey) => {
    const response = await api.post('/admin/bootstrap', { secret_key: secretKey });
    return response.data;
  },
  getRoles: async () => {
    const response = await api.get('/admin/roles');
    return response.data;
  },
  seedRoles: async () => {
    const response = await api.post('/admin/roles/seed');
    return response.data;
  },
  getStats: async () => {
    const response = await api.get('/admin/stats');
    return response.data;
  },
};

// Health API
export const healthAPI = {
  check: async () => {
    const response = await api.get('/health');
    return response.data;
  },
};

export default api;
